import type { PeerId, Quality } from '@live-stream/shared'
import {
  cameraProfile,
  sameProfile,
  sameScreenProfile,
  screenProfile,
  type ContentHint,
  type ScreenProfile,
  type SendProfile,
} from './encoding.js'
import { Peer, type TrackLabels } from './peer.js'
import type {
  ConnectionState,
  MediaTransport,
  PublishedTrack,
  PublishOptions,
  TransportEvent,
  TransportEventMap,
  TransportHandler,
  TransportStats,
  TrackKind,
  Unsubscribe,
} from './transport.js'

/**
 * Full-mesh P2P transport.
 *
 * Every participant holds a peer connection to every other participant, so
 * each client uploads N-1 copies of its own video and decodes N-1 streams.
 * That is fine at 2 and tolerable at 4; at 6 it is ~9 Mbps upstream and six
 * simultaneous decodes, and laptops thermal-throttle. The room cap enforces
 * this honestly rather than letting the call quietly fall apart.
 *
 * Phase 7 replaces this with SfuTransport behind the same interface.
 */

/**
 * The signaling surface this transport needs. Socket.IO stays out of here.
 * `tracks` labels the sender's m-sections by mid (camera, microphone, screen,
 * screen-audio) — the one thing SDP cannot say — and rides along with every
 * description, relayed as opaquely as the SDP itself.
 */
export interface MeshSignaling {
  sendDescription(to: PeerId, sdp: RTCSessionDescriptionInit, tracks: TrackLabels): void
  sendCandidate(to: PeerId, candidate: RTCIceCandidateInit): void
  onDescription(
    handler: (from: PeerId, sdp: RTCSessionDescriptionInit, tracks?: TrackLabels) => void,
  ): Unsubscribe
  onCandidate(handler: (from: PeerId, candidate: RTCIceCandidateInit) => void): Unsubscribe
  onPeerJoined(handler: (peerId: PeerId, shouldOffer: boolean) => void): Unsubscribe
  onPeerLeft(handler: (peerId: PeerId) => void): Unsubscribe
}

export interface MeshTransportOptions {
  localPeerId: PeerId
  signaling: MeshSignaling
  /** Fetches fresh ICE servers. Credentials expire, so this is called again on reconnect. */
  fetchIceServers: () => Promise<RTCIceServer[]>
  relayOnly?: boolean
}

type Handlers = {
  [E in TransportEvent]: Set<TransportHandler<E>>
}

/**
 * Used when our own credential endpoint cannot be reached.
 *
 * Losing TURN costs us the ~15% of users behind restrictive NATs, but host
 * and server-reflexive candidates still connect most peers. A degraded call
 * is strictly better than the alternative, which is a peer stuck in
 * have-local-offer forever while both people stare at their own face.
 */
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
]

export class MeshTransport implements MediaTransport {
  readonly name = 'mesh' as const

  private readonly peers = new Map<PeerId, Peer>()
  private readonly localTracks = new Map<TrackKind, MediaStreamTrack>()
  private readonly subscriptions: Unsubscribe[] = []
  private iceServers: RTCIceServer[] = []
  private closed = false
  /** The camera encoding every sender is on; follows the size of the mesh (see encoding.ts). */
  private profile: SendProfile = cameraProfile(0)
  /** What our share is: text to keep sharp, or motion to keep smooth. Set by publish, changed by the switch. */
  private screenHint: ContentHint = 'detail'
  /** The screen encoding every screen sender is on; null until we share. */
  private screen: ScreenProfile | null = null

  private readonly handlers: Handlers = {
    'track-added': new Set(),
    'track-removed': new Set(),
    'peer-joined': new Set(),
    'peer-left': new Set(),
    'connection-state': new Set(),
    stats: new Set(),
    error: new Set(),
  }

  /** False until ICE servers have arrived; signals received before then queue. */
  private ready = false
  private readonly deferred: Array<() => void> = []

  constructor(private readonly opts: MeshTransportOptions) {}

  /**
   * Run now if we can build peer connections, otherwise queue.
   *
   * An existing participant offers the moment the server announces us, which
   * is typically before getUserMedia and the credential fetch have finished.
   * A dropped offer is not a visible error — it is a call that simply never
   * connects, with both people looking at their own face — so the signals are
   * held rather than lost.
   */
  private whenReady(fn: () => void): void {
    if (this.ready) fn()
    else this.deferred.push(fn)
  }

  /**
   * Mesh ignores roomId and token: the Socket.IO connection already carries a
   * verified identity, and there is no media server to authenticate against.
   * The signature matches the interface so SfuTransport — which needs both —
   * drops in without touching a single call site.
   */
  async connect(_roomId?: string, _token?: string): Promise<void> {
    const { signaling } = this.opts

    // Subscribe BEFORE the first await. Awaiting credentials first leaves a
    // window of several hundred milliseconds in which every offer aimed at us
    // is silently dropped.
    this.subscriptions.push(
      signaling.onDescription((from, sdp, tracks) => {
        this.whenReady(() => void this.peerFor(from).handleDescription(sdp, tracks))
      }),

      signaling.onCandidate((from, candidate) => {
        // A candidate can arrive before the description that created the peer.
        this.whenReady(() => void this.peerFor(from).handleCandidate(candidate))
      }),

      signaling.onPeerJoined((peerId, shouldOffer) => {
        if (peerId === this.opts.localPeerId) return

        this.whenReady(() => {
          // A peer:joined for an id we already hold means they joined afresh:
          // a rejoin after a server restart, or after our grace for them ran
          // out. Their media plane is new, so ours for them must be too — a
          // new DTLS fingerprint cannot be negotiated onto an old connection.
          // A resume within the grace period announces nothing, so a working
          // connection is never torn down for a blip.
          const stale = this.peers.get(peerId)
          if (stale) {
            stale.close()
            this.peers.delete(peerId)
            this.emit('peer-left', { peerId })
            this.reprofile()
          }

          // Creating the peer is what matters on both sides. The transceivers
          // it adds trigger negotiation on their own, and perfect negotiation
          // resolves it if both ends offer at once — shouldOffer only avoids
          // the wasted round trip.
          const peer = this.peerFor(peerId)
          this.emit('peer-joined', { peerId })
          if (shouldOffer) void this.publishExistingTracks(peer)
        })
      }),

      signaling.onPeerLeft((peerId) => {
        this.peers.get(peerId)?.close()
        this.peers.delete(peerId)
        this.emit('peer-left', { peerId })
        // One fewer to send to: the rest may get their resolution back.
        this.reprofile()
      }),
    )

    try {
      this.iceServers = await this.opts.fetchIceServers()
    } catch (err) {
      // NEVER let this wedge the call. Anything can break a fetch — an
      // expired session, a flaky network, a browser extension that patches
      // window.fetch — and if `ready` never flips, every queued offer sits in
      // the buffer forever and the failure is completely silent.
      this.iceServers = FALLBACK_ICE_SERVERS
      this.emit('error', {
        code: 'ice_servers_unavailable',
        message:
          'Could not fetch relay credentials; falling back to public STUN. ' +
          'Connections needing a TURN relay will fail. ' +
          (err instanceof Error ? err.message : String(err)),
      })
    }

    // Set before draining, so a handler that creates a peer sees a ready
    // transport rather than re-queueing itself.
    this.ready = true

    // Drain in arrival order: a description must be applied before the
    // candidates that followed it.
    const queued = this.deferred.splice(0)
    for (const fn of queued) fn()
  }

  private peerFor(peerId: PeerId): Peer {
    let peer = this.peers.get(peerId)
    if (peer) return peer

    peer = new Peer({
      peerId,
      localPeerId: this.opts.localPeerId,
      iceServers: this.iceServers,
      ...(this.opts.relayOnly ? { relayOnly: true } : {}),
      signals: {
        sendDescription: (to, sdp, tracks) => this.opts.signaling.sendDescription(to, sdp, tracks),
        sendCandidate: (to, candidate) => this.opts.signaling.sendCandidate(to, candidate),
      },
      events: {
        onTrack: (from, kind, track, stream) => {
          this.emit('track-added', { id: track.id, peerId: from, kind, track, stream })
        },
        onTrackEnded: (from, trackId, kind) =>
          this.emit('track-removed', { id: trackId, peerId: from, kind }),
        onState: (from, state: ConnectionState) =>
          this.emit('connection-state', { peerId: from, state }),
        onError: (from, code, message) => this.emit('error', { code, message, peerId: from }),
      },
    })

    this.peers.set(peerId, peer)
    // One more to send to: everyone's encoder may have to give something up.
    this.reprofile(peer)
    void this.publishExistingTracks(peer)
    return peer
  }

  /**
   * Re-derive the camera profile from the size of the mesh. A change goes to
   * every peer; no change still goes to a newcomer, who has none yet. The
   * profile is set on the Peer before its camera sender exists, and the Peer
   * applies it when the track lands.
   */
  private reprofile(newcomer?: Peer): void {
    const next = cameraProfile(this.peers.size)
    if (sameProfile(next, this.profile)) {
      if (newcomer) void newcomer.setCameraProfile(next)
    } else {
      this.profile = next
      for (const peer of this.peers.values()) void peer.setCameraProfile(next)
    }
    this.reprofileScreen(newcomer)
  }

  /** The same for the share, while there is one: its cap follows the room, its trade-off follows the hint. */
  private reprofileScreen(newcomer?: Peer): void {
    if (!this.localTracks.has('screen')) return
    const next = screenProfile(this.peers.size, this.screenHint)
    if (sameScreenProfile(next, this.screen)) {
      if (newcomer) void newcomer.setScreenProfile(next)
      return
    }
    this.screen = next
    for (const peer of this.peers.values()) void peer.setScreenProfile(next)
  }

  /** A new peer must receive whatever we are already sending. */
  private async publishExistingTracks(peer: Peer): Promise<void> {
    for (const [kind, track] of this.localTracks) {
      await peer.setTrack(kind, track)
    }
  }

  async publish(track: MediaStreamTrack, opts: PublishOptions): Promise<PublishedTrack> {
    // contentHint is the highest-leverage line in the whole screen-share path:
    // 'detail' keeps text legible by trading frame rate, 'motion' does the
    // opposite. Simulcast is a no-op in mesh; the SFU honours it. The camera's
    // bitrate is capped per sender by the mesh's own profile, which follows
    // the size of the room (encoding.ts) — a caller's maxBitrate is not
    // consulted here, because the room decides.
    if (opts.contentHint) track.contentHint = opts.contentHint

    this.localTracks.set(opts.kind, track)
    if (opts.kind === 'screen') {
      // The profile before the senders exist, so a Peer applies it the
      // moment its screen transceiver is created rather than a poll later.
      this.screenHint = opts.contentHint === 'motion' ? 'motion' : 'detail'
      this.screen = null
      this.reprofileScreen()
    }
    await Promise.all([...this.peers.values()].map((p) => p.setTrack(opts.kind, track)))

    const forget = (): void => {
      this.localTracks.delete(opts.kind)
      if (opts.kind === 'screen') this.screen = null
    }
    return {
      id: track.id,
      kind: opts.kind,
      track,
      replace: async (next) => {
        if (next) this.localTracks.set(opts.kind, next)
        else forget()
        await Promise.all([...this.peers.values()].map((p) => p.setTrack(opts.kind, next)))
      },
      setContentHint: (hint) => {
        track.contentHint = hint
        if (opts.kind !== 'screen') return
        this.screenHint = hint === 'motion' ? 'motion' : 'detail'
        this.reprofileScreen()
      },
      stop: () => {
        track.stop()
        forget()
        void Promise.all([...this.peers.values()].map((p) => p.setTrack(opts.kind, null)))
      },
    }
  }

  async unpublish(trackId: string): Promise<void> {
    for (const [kind, track] of this.localTracks) {
      if (track.id !== trackId) continue
      this.localTracks.delete(kind)
      if (kind === 'screen') this.screen = null
      await Promise.all([...this.peers.values()].map((p) => p.setTrack(kind, null)))
      return
    }
  }

  /**
   * A no-op in mesh: without an SFU there is no one to ask for a different
   * layer. The call sites exist from Phase 4 so that Phase 7 turns this on
   * without touching the UI — it is the single biggest lever on CPU and
   * bandwidth once the SFU lands.
   */
  setSubscribedQuality(_peerId: PeerId, _trackId: string, _quality: Quality): void {}

  on<E extends TransportEvent>(event: E, handler: TransportHandler<E>): Unsubscribe {
    this.handlers[event].add(handler as never)
    return () => {
      this.handlers[event].delete(handler as never)
    }
  }

  private emit<E extends TransportEvent>(event: E, payload: TransportEventMap[E]): void {
    for (const handler of this.handlers[event]) {
      ;(handler as TransportHandler<E>)(payload)
    }
  }

  async getStats(): Promise<TransportStats> {
    const perPeerList = await Promise.all([...this.peers.values()].map((p) => p.stats()))

    const perPeer: TransportStats['perPeer'] = {}
    let rttSum = 0
    let rttCount = 0
    let lossSum = 0

    for (const s of perPeerList) {
      perPeer[s.peerId] = s
      if (s.roundTripTimeMs !== null) {
        rttSum += s.roundTripTimeMs
        rttCount++
      }
      lossSum += s.packetsLostRatio
    }

    // Encoder limits. CPU is ours wherever it shows up. Bandwidth is ours
    // only when every path reports it — on one path alone it points at that
    // peer's side of the link, and they will report it themselves.
    let cpu = false
    let bandwidthLimited = 0
    for (const s of perPeerList) {
      if (s.qualityLimitationReason === 'cpu') cpu = true
      if (s.qualityLimitationReason === 'bandwidth') bandwidthLimited++
    }
    const qualityLimitationReason: TransportStats['qualityLimitationReason'] = cpu
      ? 'cpu'
      : perPeerList.length > 0 && bandwidthLimited === perPeerList.length
        ? 'bandwidth'
        : 'none'

    return {
      outboundBitrate: 0,
      inboundBitrate: 0,
      roundTripTimeMs: rttCount > 0 ? Math.round(rttSum / rttCount) : null,
      packetsLostRatio: perPeerList.length > 0 ? lossSum / perPeerList.length : 0,
      qualityLimitationReason,
      sendProfile: this.peers.size > 0 ? this.profile : null,
      screenProfile: this.peers.size > 0 ? this.screen : null,
      perPeer,
    }
  }

  close(): void {
    if (this.closed) return
    this.closed = true

    for (const unsubscribe of this.subscriptions) unsubscribe()
    this.subscriptions.length = 0
    this.deferred.length = 0

    for (const peer of this.peers.values()) peer.close()
    this.peers.clear()

    // Local tracks are owned by whoever acquired them (useMedia), so they are
    // released there rather than stopped from under the preview.
    this.localTracks.clear()

    for (const set of Object.values(this.handlers)) set.clear()
  }
}
